<template>
  <draggable
    v-model="items"
    :disabled="disabled"
    :handle="handle"
    :force-fallback="true"
    drag-class="sortable-drag"
    fallback-class="sortable-fallback"
    @change="handleDrag"
  >
    <div v-for="(item, index) in items" :key="item[keyField]" class="bravado-draggable-items__item">
      <slot :item="item" :index="index" :update-position-handler="handleDrag" />
    </div>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable';
import { mapMutations } from 'vuex';
import { capitalize } from '~/components/utils/core/string';

export default {
  name: 'BravadoDraggableItems',
  components: {
    draggable,
  },
  filters: {
    capitalize,
  },
  props: {
    list: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    keyField: {
      type: String,
      default: 'id',
    },
    handle: {
      type: String,
      default: null,
      required: true,
    },
    model: {
      type: String,
      default: null,
    },
    updatePositionUrl: {
      type: Function,
      default: () => '',
    },
  },
  data() {
    return {
      items: [...this.list],
    };
  },
  watch: {
    list: {
      immediate: true,
      handler() {
        this.items = [...this.list];
      },
    },
  },
  methods: {
    ...mapMutations('portfolio', ['updateState']),
    async handleDrag({ moved: { element, newIndex, shift } }) {
      if (shift && element.position + shift <= 0) return;
      if (shift && element.position + shift >= this.items.length + 1) return;

      const newPosition = shift ? element.position + shift : newIndex + 1;

      this.$emit('position-updated', { element, newPosition });

      try {
        const reordered = this.moveItem({
          list: this.items,
          element,
          shift,
        });
        this.updateState({ [this.model]: reordered });
        if (this.updatePositionUrl(element)) {
          this.$notice.success(`${capitalize(this.model)} have been reordered`);
          await this.$api.$put(this.updatePositionUrl(element), { position: newPosition });
        }
        this.$emit('position-changed', [...reordered]);
      } catch (e) {
        if (this.updatePositionUrl(element)) {
          this.$notice.error(e);
        }
        this.$sentryUtils.error(e);
      }
    },
    moveItem({ list, element, shift }) {
      return shift ? this.reorder(list, element, shift) : this.updatePosition(list);
    },
    reorder(list, element, shift) {
      const elementIndex = element.position - 1;
      const from = elementIndex;
      const to = elementIndex + shift;

      const listCopy = [...list];
      listCopy.splice(from, 1);
      listCopy.splice(to, 0, element);
      return this.updatePosition(listCopy);
    },
    updatePosition(list) {
      return list.map((el, i) => ({
        ...el,
        position: i + 1,
      }));
    },
  },
};
</script>

<style scoped lang="scss">
.sortable-chosen,
.sortable-fallback,
.sortable-drag {
  border-radius: 4px;
  box-shadow: 0 8px 8px var(--black-a08), 0 0 4px var(--black-a16);

  &::after {
    border-bottom: 1px solid var(--white);
  }
}

.sortable-ghost {
  ::v-deep * {
    cursor: auto;
  }
}
</style>
